/**
 * Connection-limit guard (v1.3.35).
 *
 * Most Xtream Codes providers cap how many devices can stream from a single
 * subscription at once (`max_connections`). When the cap is hit, the provider
 * either silently drops new connections or returns an opaque error mid-load
 * — both of which look like "the channel doesn't play" to the user.
 *
 * This helper does a fast pre-flight against `player_api.php` BEFORE we hand
 * the URL to the native player, so we can show a clear "limit reached"
 * banner in Settings instead of a silent failure.
 *
 * Skip rules (do NOT block playback when):
 *   - The active playlist isn't an Xtream panel (no /player_api.php).
 *   - `max_connections` is "0" / missing — Xtream convention for unlimited.
 *   - The user_info call itself fails (network blip, panel down) — fail open.
 */
import { parseXtreamCreds } from "./xtreamApi";
import { isElectron } from "./deviceId";
import { getAssignedPlaylists } from "./playerApi";
import { webFetch } from "./webFetch";

/** ~3 second pre-flight cap so we don't make playback feel sluggish. */
const PREFLIGHT_TIMEOUT_MS = 3000;

/** Same UA + cache-busting strategy as the rest of the Xtream client. */
function fetchHeaders() {
  const ua = typeof window !== "undefined" ? window.__ZAPP_UA__ : null;
  return ua ? { "User-Agent": ua } : {};
}

async function fetchUserInfoText(url) {
  // Electron: route through main process IPC (no CORS).
  if (isElectron() && typeof window !== "undefined" && window.electronAPI?.fetchUrl) {
    const r = await window.electronAPI.fetchUrl(url);
    if (!r?.ok) throw new Error(r?.error || `HTTP ${r?.status || "?"}`);
    return r.body;
  }
  // Android Capacitor / browser fallback (v1.4.8 — routes through
  // /api/proxy/fetch in Web Player mode so CORS doesn't block).
  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), PREFLIGHT_TIMEOUT_MS);
  try {
    const r = await webFetch(url, {
      headers: fetchHeaders(),
      cache: "no-store",
      signal: ctrl.signal,
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return await r.text();
  } finally {
    clearTimeout(tid);
  }
}

async function resolveActivePlaylist() {
  const activeId = localStorage.getItem("vupp_active_playlist");
  try {
    const r = await getAssignedPlaylists();
    const list = r?.playlists || [];
    if (!list.length) return null;
    return list.find((p) => p.id === activeId) || list[0] || null;
  } catch {
    return null;
  }
}

/**
 * Run the connection-limit pre-flight.
 *
 * Returns:
 *   { ok: true }                              — allowed to play
 *   { ok: false, active, max, providerName? } — at or over the limit
 *
 * Never throws — any internal error fails open.
 */
export async function checkConnectionLimit() {
  try {
    const playlist = await resolveActivePlaylist();
    if (!playlist?.m3u_url) return { ok: true };
    const creds = parseXtreamCreds(playlist.m3u_url);
    if (!creds) return { ok: true };

    const url = new URL(creds.base);
    url.searchParams.set("username", creds.username);
    url.searchParams.set("password", creds.password);
    url.searchParams.set("_t", String(Date.now()));

    const text = await fetchUserInfoText(url.toString());
    let json = null;
    try { json = JSON.parse(text); } catch { return { ok: true }; }

    const user = json?.user_info;
    if (!user) return { ok: true };

    const max = parseInt(user.max_connections || "0", 10) || 0;
    const active = parseInt(user.active_cons || "0", 10) || 0;

    // max_connections = 0 → Xtream "unlimited". Never block.
    if (max <= 0) return { ok: true };
    if (active >= max) {
      return {
        ok: false,
        active,
        max,
        providerName: playlist?.name || null,
      };
    }
    return { ok: true };
  } catch {
    // Fail open — never block playback because of a pre-flight glitch.
    return { ok: true };
  }
}
